import { useEffect, useMemo } from "react";

import { MENTION_TYPE, SOURCE } from "@/constants";
import { useEpisodes } from "@/containers/Episodes/EpisodesContext";
import { cx, events } from "@/utils";

import { useEpisode } from "./context";
import AudioEpisodeBody from "./partials/Body/Audio";
import NewsEpisodeBody from "./partials/Body/News";
import TextEpisodeBody from "./partials/Body/Text";
import EpisodeBase from "./partials/EpisodeBase";
import EpisodeMobile from "./partials/EpisodeMobile";
import EpisodeProvider from "./provider";

import styles from "./Episode.module.css";

/**
 *
 * The props that Episode accepts are not really documented prior to this comment, but
 * since I've tried putting everything into a episode context now I've taken the time to
 * put down what I've found. Use this when typing this file or when trying to figure out
 * if something is used or not.
 *
 * Types found in EpisodeContext
 */

/** Tell episode which component should render which source */
const getBodyComponent = (episode) => {
  const { mentions } = episode;

  if (episode.source === SOURCE.NEWS) {
    return NewsEpisodeBody;
  }

  // If there's a single audio mention, render this as an audio hit.
  // The rest are meta or considered as meta.

  if (mentions.some((m) => m.context === MENTION_TYPE.TEXT)) {
    return AudioEpisodeBody;
  }

  return TextEpisodeBody;
};

const Episode = ({ className }) => {
  const episode = useEpisode();

  // props should be a Episode here when typing. But some settings may come along as well right now.
  const Body = getBodyComponent(episode);

  return (
    <EpisodeBase className={className}>
      <Body />
    </EpisodeBase>
  );
};

const ResponsiveEpisode = (props) => {
  const { mobile } = useEpisodes();
  const Component = useMemo(() => (mobile ? EpisodeMobile : Episode), [mobile]);

  // stop all playing instances when unmounting
  useEffect(
    () => () => {
      document.dispatchEvent(events.stopAllPlayingAudio);
    },
    []
  );

  return <Component {...props} />;
};

const EpisodeWithLoader = ({ className = "", loading = false, ...props }) => {
  return loading ? (
    <div className={cx(className, styles.placeholder)} />
  ) : (
    <EpisodeProvider {...props}>
      <ResponsiveEpisode className={className} />
    </EpisodeProvider>
  );
};

export default EpisodeWithLoader;
